import { useState } from "react";
import { useAccount, useBalance } from "wagmi";
import { type Address, formatUnits } from "viem";
import { Loader2, ExternalLink } from "lucide-react";
import { useBuyTokens, useSellTokens } from "../../hooks/useLaunchpad";
import { useTokenBalance } from "../../hooks/useTokenBalance";
import { formatTokenAmount, explorerUrl, cn } from "../../lib/utils";

type TradeTab = "buy" | "sell";

interface TradePanelProps {
  tokenId: bigint;
  tokenAddress: Address;
  tokenSymbol: string;
  isGraduated: boolean;
}

const QUICK_PERCENTS = [25, 50, 75, 100];

export function TradePanel({
  tokenId,
  tokenAddress,
  tokenSymbol,
  isGraduated,
}: TradePanelProps) {
  const { address: account, isConnected } = useAccount();
  const [tab, setTab] = useState<TradeTab>("buy");

  const { data: nativeBalance } = useBalance({ address: account });
  const { balance: tokenBal } = useTokenBalance(tokenAddress);

  const buy = useBuyTokens(tokenId);
  const sell = useSellTokens(tokenId, tokenAddress);

  function handleQuickPercent(pct: number) {
    if (tab === "buy") {
      if (!nativeBalance) return;
      const amount = (nativeBalance.value * BigInt(pct)) / 100n;
      buy.setBuyAmount(formatUnits(amount, 18));
    } else {
      if (tokenBal === 0n) return;
      const amount = (tokenBal * BigInt(pct)) / 100n;
      sell.setSellAmount(formatUnits(amount, 18));
    }
  }

  function getButtonText(): string {
    if (!isConnected) return "Connect Wallet";

    if (tab === "buy") {
      if (!buy.buyAmount || parseFloat(buy.buyAmount) <= 0) return "Enter Amount";
      if (buy.isPending) return "Confirming...";
      if (buy.isConfirming) return "Buying...";
      return `Buy ${tokenSymbol}`;
    } else {
      if (!sell.sellAmount || parseFloat(sell.sellAmount) <= 0) return "Enter Amount";
      if (sell.isApproving) return "Approving...";
      if (sell.needsApproval) return `Approve ${tokenSymbol}`;
      if (sell.isPending) return "Confirming...";
      if (sell.isConfirming) return "Selling...";
      return `Sell ${tokenSymbol}`;
    }
  }

  function handleAction() {
    if (tab === "buy") {
      buy.executeBuy();
    } else {
      if (sell.needsApproval) {
        sell.approve();
      } else {
        sell.executeSell();
      }
    }
  }

  const isLoading =
    tab === "buy"
      ? buy.isPending || buy.isConfirming
      : sell.isPending || sell.isConfirming || sell.isApproving;

  const currentTxHash = tab === "buy" ? buy.txHash : sell.txHash;

  return (
    <div className="glass-card p-5">
      <div className="flex gap-1 p-1 bg-surface/50 rounded-button mb-5">
        <button
          onClick={() => setTab("buy")}
          className={cn(
            "flex-1 py-2.5 rounded-button text-sm font-semibold transition-all",
            tab === "buy"
              ? "bg-success/15 text-success"
              : "text-text-secondary hover:text-text-primary"
          )}
        >
          Buy
        </button>
        <button
          onClick={() => setTab("sell")}
          className={cn(
            "flex-1 py-2.5 rounded-button text-sm font-semibold transition-all",
            tab === "sell"
              ? "bg-error/15 text-error"
              : "text-text-secondary hover:text-text-primary"
          )}
        >
          Sell
        </button>
      </div>

      {tab === "buy" ? (
        <div className="space-y-3">
          <div className="token-input-row">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-tertiary">You Pay</span>
              <span className="text-xs text-text-tertiary">
                Balance: {nativeBalance ? formatTokenAmount(nativeBalance.value) : "0"} JASMY
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={buy.buyAmount}
                onChange={(e) => buy.setBuyAmount(e.target.value)}
                placeholder="0.0"
                className="amount-input"
                min="0"
                step="any"
              />
              <span className="text-sm font-semibold text-text-secondary shrink-0">
                JASMY
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {QUICK_PERCENTS.map((pct) => (
              <button
                key={pct}
                onClick={() => handleQuickPercent(pct)}
                className="flex-1 py-1.5 rounded-lg text-xs font-medium bg-surface border border-border text-text-secondary hover:text-text-primary hover:border-border-bright transition-all"
              >
                {pct}%
              </button>
            ))}
          </div>

          {buy.quotedTokensOut > 0n && (
            <div className="p-3 bg-surface/30 rounded-button border border-border">
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary">You Receive</span>
                <span className="text-success font-medium">
                  ~{formatTokenAmount(buy.quotedTokensOut)} {tokenSymbol}
                </span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="token-input-row">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-tertiary">You Sell</span>
              <span className="text-xs text-text-tertiary">
                Balance: {formatTokenAmount(tokenBal)} {tokenSymbol}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={sell.sellAmount}
                onChange={(e) => sell.setSellAmount(e.target.value)}
                placeholder="0.0"
                className="amount-input"
                min="0"
                step="any"
              />
              <span className="text-sm font-semibold text-text-secondary shrink-0">
                {tokenSymbol}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {QUICK_PERCENTS.map((pct) => (
              <button
                key={pct}
                onClick={() => handleQuickPercent(pct)}
                className="flex-1 py-1.5 rounded-lg text-xs font-medium bg-surface border border-border text-text-secondary hover:text-text-primary hover:border-border-bright transition-all"
              >
                {pct}%
              </button>
            ))}
          </div>

          {sell.quotedJasmyOut > 0n && (
            <div className="p-3 bg-surface/30 rounded-button border border-border">
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary">You Receive</span>
                <span className="text-error font-medium">
                  ~{formatTokenAmount(sell.quotedJasmyOut)} JASMY
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        onClick={handleAction}
        disabled={!isConnected || isLoading}
        className={cn(
          "w-full mt-4 py-3 px-6 rounded-button font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed",
          tab === "buy"
            ? "bg-success hover:bg-success/90 text-background"
            : "bg-error hover:bg-error/90 text-white"
        )}
      >
        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        {getButtonText()}
      </button>

      {currentTxHash && (
        <div className="mt-3 text-center">
          <a
            href={explorerUrl("tx", currentTxHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
          >
            View Transaction <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {isGraduated && (
        <div className="mt-3 p-3 bg-secondary/10 border border-secondary/20 rounded-button text-xs text-secondary text-center">
          This token has graduated. Trading via V2 Router.
        </div>
      )}
    </div>
  );
}
